import { useBoolean } from 'ahooks';
import { Typography } from 'antd';
import { isEqual, omit } from 'lodash';
import { Moment } from 'moment';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from 'react-query';
import { User, UserId } from 'api/models';
import { ProjectPreview } from 'api/notifications';
import { Project, ProjectPhase } from 'api/project';
import { Task } from 'api/tasks';
import {
  TimesheetAPI,
  TimesheetEntryRequest,
  TimesheetEntryType,
  TimesheetSourceType,
  TimesheetUISourceType,
} from 'api/timesheets';
import { Category } from 'api/timesheets/categories';
import { SubmissionHoursConstrainType } from 'api/timesheets/configuration/submission-hours';
import { Option } from 'components/atoms/select/types';
import { ToastDescription, toastService } from 'components/molecules/toast';
import {
  convertRowDataToTimesheet,
  deleteEntryFromTimesheetRows,
  findAdhocTaskInTimesheetRows,
  findPhaseInTimesheetRows,
  findProjectInTimesheetRows,
  findRowInTimesheetRows,
  findTaskInTimesheetRows,
  findTimeEntryinState,
  getUniqueAdhocActivitiesFromEntries,
  getUniquePhasesFromEntries,
  getUniqueProjectsFromEntries,
  isSameTask,
  removeDuplicateEntry,
  transformEntriesIntoWeeklyRows,
  updateCategory,
} from 'components/organisms/timesheets/common/time-entry-utils';
import {
  asList,
  Categories,
  useCategories,
} from 'components/pages/timesheet-settings/categories';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import { useProjectsQuery } from 'queries';
import { useInvalidateCalendarIntegration } from 'queries/timesheets/calendar-integration-actions';
import { useTimesheetConfiguration } from 'queries/timesheets/configuration';
import { getRejectNoteQueryKey } from 'queries/timesheets/reject-note';
import { fullName } from 'utils/full-name';
import { floatUid } from 'utils/uid';
import { unreachable } from 'utils/unreachable';
import { getDays } from './common/utils';
import { AdhocTask, CalendarIntegrationTask } from './drawers';
import {
  PhaseTask,
  PhaseTaskPreview,
  ProjectRow,
  ProjectTask,
  TimeEntryState,
  TimeEntryTaskPreview,
  Timesheet,
  TimesheetAdhocTask,
  TimesheetCalendarIntegrationEntry,
  TimesheetCalendarTask,
  TimesheetEntry,
  TimesheetStatus,
  TimesheetTableTimeEntry,
  TimesheetTask,
  TimesheetTaskEntry,
  TimesheetWeeklyRow,
  TimesheetWeeklyRowTask,
  TimesheetWeekSummary,
} from './types';
import { getTimesheetHandleError, isTimeEntrySubmittable } from './utils';

export const useTimesheetActions = (
  startDate: Moment,
  user: User,
  isMyTimesheet?: boolean,
  projectId?: number,
): {
  projects: Project[] | undefined;
  timeEntryCategories: Category[] | undefined;
  categories: Categories;
  timesheetWeekRows: TimesheetWeeklyRow[] | undefined;
  timesheetEntries: TimesheetEntry[];
  isTimesheetLoading: boolean;
  isStatusChangeInProgress: boolean;
  timesheet: Timesheet | undefined;
  addTasksToTimesheet: (tasks: Task[]) => void;
  addAdhocActivitiesToTimesheet: (task: AdhocTask[]) => void;
  updateTimeEntryInTask: (
    task: TimesheetWeeklyRowTask,
    entryAt: string,
    timeEntry: TimesheetTableTimeEntry,
  ) => void;
  deleteTimeEntryInTask: (
    task: TimesheetWeeklyRowTask,
    entryAt: string,
    timeEntryId: number,
  ) => void;
  addTimeEntryInTask: (task: TimesheetWeeklyRowTask, entryAt: string) => void;
  editAdhocTaskName: (
    timesheetRows: TimesheetWeeklyRow,
    activityName: string,
    UserId: UserId,
  ) => void;
  getSummaryDetail: (
    timesheetRows: TimesheetWeeklyRow[],
  ) => TimesheetWeekSummary;
  deleteTask: (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
  ) => void;
  unsubmitTask: (task: TimesheetWeeklyRowTask) => void;
  approveTask: (task: TimesheetWeeklyRowTask, projectId?: number) => void;
  rejectTask: (
    task: TimesheetWeeklyRowTask,
    content?: string,
    projectId?: number,
  ) => Promise<void>;
  withdrawTask: (task: TimesheetWeeklyRowTask, projectId?: number) => void;
  submitTimesheet: () => void;
  rejectTimesheet: (content?: string, projectId?: number) => Promise<void>;
  unsubmitTimesheet: (timeEntryIds?: number[]) => void;
  approveTimesheet: (projectId?: number) => void;
  withdrawTimesheet: (projectId?: number) => void;
  sendReminder: () => void;
  getPreviousWeekTasks: () => void;
  timeSheetSummary: TimesheetWeekSummary;
  addIntegrationTaskToTimesheet: (
    task: TimesheetCalendarIntegrationEntry[],
  ) => void;
  addProjectToTimesheet: (projectToBeAdded: Option) => void;
  projectOptions: Option[];
  addEntityToProject: (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
    entity: any,
  ) => void;
  changeProject: (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
    projectToBeAdded: Option,
  ) => void;
  addActivityToProject: (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
    activityName: string,
  ) => void;
} => {
  const { data: projects } = useProjectsQuery({
    refetchOnMount: 'always',
  });

  const { categories: _categories } = useCategories();
  const timeEntryCategories = useMemo(
    () => asList(_categories).map(([_, x]) => x),
    [_categories],
  );
  const invalidateCalendarIntegration = useInvalidateCalendarIntegration();
  const { data: configuration } = useTimesheetConfiguration();

  const [isTimesheetLoading, setIsTimesheetLoading] = useState(true);
  const [isStatusChangeInProgress, setIsStatusChangeInProgress] =
    useState(false);
  const [timesheet, setTimesheet] = useState<Timesheet>();
  const [timesheetEntries, setTimesheetEntries] = useState<TimesheetEntry[]>(
    [],
  );

  const timesheetEntriesRef = useRef(timesheetEntries);

  const [_refresh, { toggle: refreshData }] = useBoolean(false);

  const [timesheetWeekRows, setTimesheetWeekRows] = useState<
    TimesheetWeeklyRow[]
  >([]);

  const timesheetWeekRowsRef = useRef(timesheetWeekRows);

  const queryClient = useQueryClient();

  useEffect(() => {
    timesheetEntriesRef.current = timesheetEntries;
  }, [timesheetEntries]);

  useEffect(() => {
    timesheetWeekRowsRef.current = timesheetWeekRows;
  }, [timesheetWeekRows]);

  const translate = useTranslate();
  const handleError = getTimesheetHandleError(user, translate, refreshData);

  useEffect(() => {
    setIsTimesheetLoading(true);
    TimesheetAPI.getTimesheetForUser(
      user.userId,
      startDate.format('YYYY-MM-DD'),
      projectId,
    ).then((userTimesheet: Timesheet) => {
      setTimesheet(userTimesheet);
      setIsTimesheetLoading(false);
    });
  }, [setTimesheetWeekRows, startDate, user.userId, _refresh, projectId]);

  const getNewTimeEntry = useCallback(
    (task: TimesheetWeeklyRowTask, entryDate: string) => {
      let category;

      if (task.defaultCategoryId) {
        category = timeEntryCategories.find(
          (c) => (c.id as unknown as number) === task.defaultCategoryId,
        );
      } else {
        const previousDefault = timesheetWeekRowsRef.current.find((e) =>
          isSameTask(e.task, task),
        );

        category = timeEntryCategories.find(
          (c) =>
            (c.id as unknown as number) ===
            previousDefault?.task.defaultCategoryId,
        );
      }
      const commonProps = {
        timeEntryId: floatUid() * -1,
        minutes: 0,
        date: entryDate,
        userId: user.userId,
        inState: TimeEntryState.NotCreated,
      };

      switch (task.entryType) {
        case TimesheetEntryType.TaskEntry:
          return {
            ...commonProps,
            billable: task.defaultBillable ?? true,
            category: category
              ? { categoryId: category.id, categoryName: category.name }
              : undefined,
            task,
            type: task.entryType,
          };
        case TimesheetEntryType.AdhocEntry:
          return {
            ...commonProps,
            billable: !!task.defaultBillable,
            category: category
              ? { categoryId: category.id, categoryName: category.name }
              : undefined,
            type: task.entryType,
            activityName: task.activityName,
            project: task.project,
          };
        case TimesheetEntryType.Integration:
          return {
            ...commonProps,
            billable: !!task.defaultBillable,
            category: category
              ? { categoryId: category.id, categoryName: category.name }
              : undefined,
            type: task.entryType,
            activityName: task.activityName,
            project: task.project,
            sourceId: task.sourceId,
          };
        case TimesheetEntryType.ProjectEntry:
          return {
            ...commonProps,
            billable: task.billable ?? true,
            category: category
              ? { categoryId: category.id, categoryName: category.name }
              : undefined,
            type: TimesheetEntryType.ProjectEntry,
            project: task.project,
          };
        case TimesheetEntryType.PhaseEntry:
          return {
            ...commonProps,
            billable: task.billable ?? true,
            category: category
              ? { categoryId: category.id, categoryName: category.name }
              : undefined,
            type: TimesheetEntryType.PhaseEntry,
            project: task.project,
            projectPhase: task.projectPhase,
          };
        default:
          return unreachable(task);
      }
    },
    [user, timeEntryCategories],
  );

  useEffect(() => {
    const entries = timesheet?.entries ?? [];
    const transformedRows = transformEntriesIntoWeeklyRows(entries, projects);

    const currentWeekDays = getDays(startDate, 7).map((x) =>
      x.format('YYYY-MM-DD'),
    );

    const resultRows = transformedRows.reduce(
      (previousRows, { task, days }) => {
        const resultDays = currentWeekDays.reduce((previousDays, day) => {
          if (previousDays[day] == null) {
            const entry = getNewTimeEntry(task, day);

            entries.push(entry);

            return { ...previousDays, [day]: [entry] };
          }

          return previousDays;
        }, days);

        return [...previousRows, { task, days: resultDays }];
      },
      [] as TimesheetWeeklyRow[],
    );

    setTimesheetEntries(entries);
    setTimesheetWeekRows(resultRows);
  }, [timesheet, startDate, getNewTimeEntry, projects]);

  const taskAsTimesheetTask = (task: Task): TimesheetTask => {
    const getTaskField = (fields, fieldName) =>
      fields.find((field) => field.fieldName === fieldName);

    return {
      id: task.taskId,
      name: task.taskName,
      status: getTaskField(task.fields, 'Status')?.fieldValue,
      progress: task.progress,
      type: task.type,
      project: {
        id: task.project.projectId,
        name: task.project.projectName,
      },
      defaultBillable: task.defaultTimeEntryBillable ?? true,
      defaultCategoryId: task.defaultTimeEntryCategory ?? null,
      entryType: TimesheetEntryType.TaskEntry,
      sourceType: TimesheetUISourceType.Task,
      sourceId: task.taskId,
    } as TimesheetTask;
  };

  const taskAsPhaseEntry = (phaseTobeAdded) => {
    return {
      project: { ...phaseTobeAdded.project },
      projectPhase: {
        id: phaseTobeAdded.projectPhaseId,
        name: phaseTobeAdded.projectPhaseName,
        deleted: false,
      },
      entryType: TimesheetEntryType.PhaseEntry,
      private: phaseTobeAdded.private,
      status: phaseTobeAdded?.fields[0].fieldValue,
      progress: phaseTobeAdded?.progress,
      startDate: phaseTobeAdded?.startDate,
      dueDate: phaseTobeAdded?.dueDate,
      sourceId: phaseTobeAdded?.projectPhaseId,
      sourceType: TimesheetUISourceType.Phase,
      billable: phaseTobeAdded.billable ?? true,
      approvalStatus: phaseTobeAdded.status,
      defaultCategoryId: phaseTobeAdded.defaultCategoryId,
    } as PhaseTask;
  };

  const taskAsProjectRow = (projectToBeAdded: ProjectTask) => {
    return {
      project:
        projectToBeAdded.project.value !== 1
          ? ({
              name: projectToBeAdded.project.label,
              id: projectToBeAdded.project.value as number,
            } as ProjectPreview)
          : undefined,
      defaultCategoryId: projectToBeAdded.category?.categoryId,
      entryType: TimesheetEntryType.ProjectEntry,
      sourceId: projectToBeAdded.project.value,
      sourceType: TimesheetUISourceType.Project,
      billable: projectToBeAdded.billable ?? true,
    } as ProjectRow;
  };

  const getTasksToAddToTimesheet = (
    taskList: (Task | TimeEntryTaskPreview)[],
  ) => {
    return taskList.reduce(
      (previous, task) => {
        if (
          findTaskInTimesheetRows(
            timesheetWeekRows,
            'taskId' in task ? task.taskId : task.id,
          )
        ) {
          return previous;
        }
        let newTask: TimesheetTask;

        if ('taskId' in task) {
          newTask = taskAsTimesheetTask(task);
        } else {
          newTask = {
            ...task,
            entryType: TimesheetEntryType.TaskEntry,
            sourceType: TimesheetUISourceType.Task,
            sourceId: task.id,
            approvalStatus: TimesheetStatus.NotSubmitted,
            defaultCategoryId: task.defaultCategoryId,
          };
        }

        const entries = getDays(startDate, 7).map((x) =>
          getNewTimeEntry(newTask, x.format('YYYY-MM-DD')),
        );

        return {
          weeklyRows: [
            ...previous.weeklyRows,
            {
              task: newTask,
              days: entries.reduce(
                (previousEntries, current) => ({
                  ...previousEntries,
                  [current.date]: [current],
                }),
                {},
              ),
            },
          ],
          timeEntries: [...previous.timeEntries, ...entries],
        };
      },
      {
        weeklyRows: [] as TimesheetWeeklyRow[],
        timeEntries: [] as TimesheetEntry[],
      },
    );
  };

  const addTasksToTimesheet = (taskList: (Task | TimeEntryTaskPreview)[]) => {
    const result = getTasksToAddToTimesheet(taskList);

    setTimesheetEntries([...timesheetEntries, ...result.timeEntries]);
    setTimesheetWeekRows([...timesheetWeekRows, ...result.weeklyRows]);
  };

  const addGenericTaskToTimesheet = (
    tasks: Array<AdhocTask | CalendarIntegrationTask>,
    entryType: TimesheetEntryType.Integration | TimesheetEntryType.AdhocEntry,
  ) => {
    return tasks.reduce(
      (previous, task) => {
        if (
          findAdhocTaskInTimesheetRows(
            timesheetWeekRows,
            task.activityName,
            task.project.value !== 1
              ? (task.project.value as number)
              : undefined,
          )
        ) {
          return previous;
        }

        const commonProps = {
          activityName: task.activityName,
          project:
            task.project.value !== 1
              ? ({
                  name: task.project.label,
                  id: task.project.value as number,
                } as ProjectPreview)
              : undefined,
          entryType,
          defaultBillable: task.billable,
          defaultCategoryId: task.category ? task.category.value : undefined,
        };

        const newTask =
          entryType === TimesheetEntryType.AdhocEntry
            ? ({
                ...commonProps,
                sourceType: TimesheetUISourceType.Adhoc as TimesheetSourceType,
              } as TimesheetAdhocTask)
            : ({
                ...commonProps,
                sourceType: (task as CalendarIntegrationTask)
                  .sourceType as TimesheetSourceType,
                sourceId: (task as CalendarIntegrationTask).sourceId,
              } as TimesheetCalendarTask);

        const entries = getDays(startDate, 7).map((x) =>
          getNewTimeEntry(newTask, x.format('YYYY-MM-DD')),
        );

        const newRow = {
          task: newTask,
          days: entries.reduce(
            (result, current) => ({ ...result, [current.date]: [current] }),
            {},
          ),
        };

        return {
          weeklyRows: [...previous.weeklyRows, newRow],
          timeEntries: [...previous.timeEntries, ...entries],
        };
      },
      {
        weeklyRows: [] as TimesheetWeeklyRow[],
        timeEntries: [] as TimesheetEntry[],
      },
    ) as {
      weeklyRows: TimesheetWeeklyRow[];
      timeEntries: TimesheetEntry[];
    };
  };

  const addAdhocActivitiesToTimesheet = (tasks: AdhocTask[]) => {
    const newEntries = addGenericTaskToTimesheet(
      tasks,
      TimesheetEntryType.AdhocEntry,
    );

    setTimesheetEntries([...timesheetEntries, ...newEntries.timeEntries]);
    setTimesheetWeekRows([...timesheetWeekRows, ...newEntries.weeklyRows]);
  };

  const addProjectsToTimesheet = (projectList: ProjectTask[]) => {
    return projectList.reduce(
      (previous, task) => {
        if (
          findProjectInTimesheetRows(
            timesheetWeekRows,
            Number(task.project.value),
          )
        ) {
          return previous;
        }

        const newTask: ProjectRow = taskAsProjectRow(task);

        const entries = getDays(startDate, 7).map((x) =>
          getNewTimeEntry(newTask, x.format('YYYY-MM-DD')),
        );

        return {
          weeklyRows: [
            ...previous.weeklyRows,
            {
              task: newTask,
              days: entries.reduce(
                (previousEntries, current) => ({
                  ...previousEntries,
                  [current.date]: [current],
                }),
                {},
              ),
            },
          ],
          timeEntries: [...previous.timeEntries, ...entries],
        };
      },
      {
        weeklyRows: [] as TimesheetWeeklyRow[],
        timeEntries: [] as TimesheetEntry[],
      },
    );
  };

  const addPhasesToTimesheet = (phaseList: PhaseTaskPreview[]) => {
    return phaseList.reduce(
      (previous, task) => {
        if (
          findPhaseInTimesheetRows(
            timesheetWeekRows,
            task.projectPhase.id,
            task.project.id,
          )
        ) {
          return previous;
        }

        const project = projects?.find(
          (x: Project) => x.projectId === task.project.id,
        );
        const phase = project?.projectPhases?.find(
          (x: ProjectPhase) => x.projectPhaseId === task.projectPhase.id,
        );

        const newTask: PhaseTask = taskAsPhaseEntry({
          ...phase,
          project: { ...task.project },
          defaultCategoryId: task.category?.categoryId,
        });

        const entries = getDays(startDate, 7).map((x) =>
          getNewTimeEntry(newTask, x.format('YYYY-MM-DD')),
        );

        return {
          weeklyRows: [
            ...previous.weeklyRows,
            {
              task: newTask,
              days: entries.reduce(
                (previousEntries, current) => ({
                  ...previousEntries,
                  [current.date]: [current],
                }),
                {},
              ),
            },
          ],
          timeEntries: [...previous.timeEntries, ...entries],
        };
      },
      {
        weeklyRows: [] as TimesheetWeeklyRow[],
        timeEntries: [] as TimesheetEntry[],
      },
    );
  };

  const getPreviousWeekTasks = () => {
    TimesheetAPI.getTimesheetForUser(
      user.userId,
      startDate.subtract(1, 'week').format('YYYY-MM-DD'),
    ).then((userTimesheet: Timesheet) => {
      const isTaskEntryType = (entry: TimesheetEntry): boolean =>
        entry !== null &&
        entry.type === TimesheetEntryType.TaskEntry &&
        !entry.task.deleted &&
        !entry.project.archived &&
        !entry.project.deleted;
      const taskArray: TimeEntryTaskPreview[] = userTimesheet.entries
        .filter((entry) => isTaskEntryType(entry))
        .map(
          (entry) =>
            ({
              ...(entry as TimesheetTaskEntry).task,
              project: { ...entry.project },
            } as TimeEntryTaskPreview),
        );

      const uniqueTaskArray = [
        ...new Map(
          taskArray.map((item: TimeEntryTaskPreview) => [item.id, item]),
        ).values(),
      ];

      const uniqueActivityArray = getUniqueAdhocActivitiesFromEntries(
        userTimesheet.entries,
      );

      const uniqueProjectArray = getUniqueProjectsFromEntries(
        userTimesheet.entries,
      );

      const uniquePhaseArray = getUniquePhasesFromEntries(
        userTimesheet.entries,
      );

      const taskEntries = getTasksToAddToTimesheet(uniqueTaskArray);

      const activityEntries = addGenericTaskToTimesheet(
        uniqueActivityArray,
        TimesheetEntryType.AdhocEntry,
      );

      const projectEntries = addProjectsToTimesheet(uniqueProjectArray);

      const phaseEntries = addPhasesToTimesheet(uniquePhaseArray);

      if (
        taskEntries.weeklyRows.length === 0 &&
        activityEntries.weeklyRows.length === 0 &&
        projectEntries.weeklyRows.length === 0 &&
        phaseEntries.weeklyRows.length === 0
      ) {
        toastService({
          title: translate('timesheets.toast.titles.no_entries'),
          message: '',
        }).info();
      }

      setTimesheetEntries([
        ...timesheetEntries,
        ...activityEntries?.timeEntries,
        ...taskEntries?.timeEntries,
        ...projectEntries?.timeEntries,
        ...phaseEntries?.timeEntries,
      ]);
      setTimesheetWeekRows([
        ...timesheetWeekRows,
        ...activityEntries?.weeklyRows,
        ...taskEntries?.weeklyRows,
        ...projectEntries?.weeklyRows,
        ...phaseEntries?.weeklyRows,
      ]);
    });
  };

  const editAdhocTaskName = (
    timesheetRow: TimesheetWeeklyRow,
    newActivityName: string,
    userId: UserId,
  ) => {
    const update = Object.keys(timesheetRow.days)
      .map((key) => {
        return [
          ...timesheetRow.days[key].map((entry) => ({
            timeEntryId: entry.timeEntryId,
            minutes: entry.minutes,
            date: key,
            userId: userId[1],
            billable: entry.billable,
            activityName: newActivityName,
            entryType: TimesheetEntryType.AdhocEntry,
            type: TimesheetEntryType.AdhocEntry,
          })),
        ];
      })
      .flat()
      .filter((entry) => entry.minutes > 0);

    const rows = timesheetWeekRows.reduce(
      (previous: TimesheetWeeklyRow[], curr: TimesheetWeeklyRow) => {
        if (
          'activityName' in curr.task &&
          'activityName' in timesheetRow.task &&
          curr.task.activityName === timesheetRow.task.activityName &&
          curr.task.project?.id === timesheetRow.task.project?.id
        ) {
          return [
            ...previous,
            {
              ...curr,
              task: { ...curr.task, activityName: newActivityName },
            },
          ];
        }

        return [...previous, curr];
      },
      [],
    );

    const timeEntryIds = Object.keys(timesheetRow.days)
      .map((day) => timesheetRow.days[day].map((entry) => entry.timeEntryId))
      .flat();

    const entries = timesheetEntries.reduce(
      (previous: TimesheetEntry[], curr: TimesheetEntry) => {
        if (timeEntryIds.includes(curr.timeEntryId)) {
          return [...previous, { ...curr, activityName: newActivityName }];
        }

        return [...previous, curr];
      },
      [],
    );

    const tempWeekRows = timesheetWeekRows;
    const tempTimeEntries = timesheetEntries;

    setTimesheetWeekRows(rows);
    setTimesheetEntries(entries);

    TimesheetAPI.editAdhocTask(userId[1], { update })
      .then(() =>
        toastService({
          title: translate('changes_saved' as TranslationKey),
          message: (
            <ToastDescription
              message={translate('changes_saved.extra' as TranslationKey)}
            />
          ),
        }).success(),
      )
      .catch(() => {
        setTimesheetWeekRows(tempWeekRows);
        setTimesheetEntries(tempTimeEntries);
        toastService({
          title: translate('error_saving' as TranslationKey),
          message: (
            <ToastDescription message={translate('error' as TranslationKey)} />
          ),
        }).error();
      });
  };

  const getSummaryDetail = (timesheetRows: TimesheetWeeklyRow[]) => {
    const totalMinutesForDays = {};
    let totalMinutesForWeek = 0;

    timesheetRows.forEach((timesheetRow) => {
      Object.keys(timesheetRow.days).forEach((entryAt) => {
        const minutesForDayPerTask = timesheetRow.days[entryAt].reduce(
          (total, entry) => total + entry.minutes,
          0,
        );

        totalMinutesForDays[entryAt] =
          (totalMinutesForDays[entryAt] ?? 0) + minutesForDayPerTask;
        totalMinutesForWeek += minutesForDayPerTask;
      });
    });

    return {
      totalMinutesForDays,
      totalMinutesForWeek,
    };
  };

  const updatedTotalTime = (timeEntryId: number, minutes: number) => {
    const totalTime = timesheet?.totalTime
      ? timesheet?.totalTime +
        getSummaryDetail(timesheetWeekRows ?? []).totalMinutesForWeek
      : getSummaryDetail(timesheetWeekRows ?? []).totalMinutesForWeek;

    if (timeEntryId > 0) {
      const updatedTimeEntry = timesheetEntries.find(
        (entry) => entry.timeEntryId === timeEntryId,
      );

      if (updatedTimeEntry) {
        return totalTime - updatedTimeEntry.minutes + minutes;
      }

      return totalTime;
    }

    return totalTime + minutes;
  };

  const updateTimeEntriesState = useCallback(
    (timeEntryId: number, timeEntryValue: TimesheetTableTimeEntry) => {
      const modifiedEntries = timesheetEntriesRef.current.map((entry) => {
        if (entry.timeEntryId === timeEntryId) {
          return {
            ...entry,
            ...timeEntryValue,
          };
        }

        return entry;
      });

      setTimesheetEntries(modifiedEntries);

      const timeEntry = findTimeEntryinState(
        modifiedEntries,
        timeEntryValue.timeEntryId,
      );

      if (!timeEntry) {
        return;
      }

      if (timesheetWeekRowsRef.current) {
        const timesheetTask = findRowInTimesheetRows(
          timesheetWeekRowsRef.current,
          timeEntry,
        )?.task;

        if (!timesheetTask) {
          return;
        }
        const newTimesheetRows = timesheetWeekRowsRef.current.map(
          (timesheetRow) => {
            if (isSameTask(timesheetRow.task, timesheetTask)) {
              const { days } = timesheetRow;
              const day = days[timeEntry.date];

              return {
                ...timesheetRow,
                days: {
                  ...days,
                  [timeEntry.date]: day.map((childEntry) => {
                    if (childEntry.timeEntryId === timeEntryId) {
                      return { ...childEntry, ...timeEntry };
                    }

                    return childEntry;
                  }),
                },
              };
            }

            return timesheetRow;
          },
        );

        setTimesheetWeekRows(newTimesheetRows);
      }
    },
    [timesheetWeekRowsRef, timesheetEntriesRef],
  );

  const deleteTimeEntryFromState = (timeEntryId: number): boolean => {
    const modifiedEntries = timesheetEntries.filter(
      (entry) => entry.timeEntryId !== timeEntryId,
    );

    setTimesheetEntries(modifiedEntries);

    const timeEntry = findTimeEntryinState(timesheetEntries, timeEntryId);

    if (!timeEntry) {
      return false;
    }
    if (timesheetWeekRows) {
      const modifiedRows = deleteEntryFromTimesheetRows(
        timesheetWeekRows,
        timeEntry,
      );
      const row = findRowInTimesheetRows(modifiedRows, timeEntry);

      setTimesheetWeekRows(modifiedRows);
      timesheetWeekRowsRef.current = modifiedRows;

      return row?.days[timeEntry.date].length === 0;
    }

    return false;
  };

  const deleteTimeEntriesFromState = (
    timeEntries: TimesheetEntry[],
    task: TimesheetWeeklyRow,
  ) => {
    const idsToBeDeleted = timeEntries.map((entry) => entry.timeEntryId);

    const modifiedEntries = timesheetEntries.filter(
      (entry) => !idsToBeDeleted.includes(entry.timeEntryId),
    );

    setTimesheetEntries(modifiedEntries);
    if (timesheetWeekRows && task) {
      const newTimesheetRows = timesheetWeekRows.filter(
        (timesheetRow) =>
          !(
            isSameTask(timesheetRow.task, task.task) &&
            isEqual(timesheetRow.days, task.days)
          ),
      );

      if (newTimesheetRows.length < 1 && timesheet) {
        setTimesheet({
          ...timesheet,
          status: TimesheetStatus.NotSubmitted,
        });
      }

      setTimesheetWeekRows(newTimesheetRows);
    }
  };
  const addTimeEntryToState = (timeEntry: TimesheetEntry) => {
    const modifiedEntries = findTimeEntryinState(
      timesheetEntriesRef.current,
      timeEntry.timeEntryId,
    )
      ? timesheetEntriesRef.current
      : timesheetEntriesRef.current.concat(timeEntry);

    setTimesheetEntries(modifiedEntries);
    if (timesheetWeekRowsRef.current) {
      const timesheetTask = findRowInTimesheetRows(
        timesheetWeekRowsRef.current,
        timeEntry,
      )?.task;

      if (!timesheetTask) {
        return;
      }
      const newTimesheetRows = timesheetWeekRowsRef.current.map(
        (timesheetRow) => {
          if (isSameTask(timesheetRow.task, timesheetTask)) {
            const { days } = timesheetRow;
            const day = days[timeEntry.date];

            if (timeEntry.type === TimesheetEntryType.Integration) {
              const updatedDay = day?.filter((entry) => entry.minutes !== 0);

              return {
                ...timesheetRow,
                days: {
                  ...days,
                  [timeEntry.date]: (updatedDay ?? []).concat(timeEntry),
                },
              };
            }
            const isEntryAlreadyPresent = day?.find(
              ({ timeEntryId }) => timeEntryId === timeEntry.timeEntryId,
            );

            return {
              ...timesheetRow,
              days: {
                ...days,
                [timeEntry.date]: isEntryAlreadyPresent
                  ? day
                  : (day ?? []).concat(timeEntry),
              },
            };
          }

          return timesheetRow;
        },
      );

      setTimesheetWeekRows(newTimesheetRows);
    }
  };

  const addTimeEntriesToState = (timeEntries: TimesheetEntry[]) => {
    let modifiedEntries = timesheetEntries;

    timeEntries.forEach((entry) => {
      if (!findTimeEntryinState(modifiedEntries, entry.timeEntryId)) {
        modifiedEntries = timesheetEntries.concat(entry);
      }
    });
    setTimesheetEntries(modifiedEntries);
    setTimesheetWeekRows(
      transformEntriesIntoWeeklyRows(modifiedEntries, projects),
    );
  };

  const checkForSubmissionHourRestrict = (
    task: TimesheetWeeklyRowTask,
    timeEntryId: number,
    minutes: number,
  ) => {
    if (
      configuration &&
      configuration.configMeta.submissionHours.constraintType !==
        SubmissionHoursConstrainType.IsEmpty
    ) {
      if (
        (task?.approvalStatus &&
          task?.approvalStatus !== TimesheetStatus.NotSubmitted) ||
        timesheet?.status === TimesheetStatus.Submitted
      ) {
        const {
          submissionHours: { constraintType, value },
        } = configuration.configMeta;
        const updateTotalTime = updatedTotalTime(timeEntryId, minutes);

        switch (constraintType) {
          case SubmissionHoursConstrainType.GreaterThan:
            if (value * 60 > updateTotalTime) {
              toastService({
                title: translate(
                  'timesheets.feature.update.failed' as TranslationKey,
                ),
                message: (
                  <ToastDescription
                    message={translate(
                      'timesheets.feature.update.fail.min.required' as TranslationKey,
                      {
                        hours: value,
                      },
                    )}
                  />
                ),
              }).error();

              return false;
            }

            break;

          case SubmissionHoursConstrainType.LessThan:
            if (value * 60 < updateTotalTime) {
              toastService({
                title: translate(
                  'timesheets.feature.update.failed' as TranslationKey,
                ),
                message: (
                  <ToastDescription
                    message={translate(
                      'timesheets.feature.update.fail.max.required' as TranslationKey,
                      {
                        hours: value,
                      },
                    )}
                  />
                ),
              }).error();

              return false;
            }
            break;

          case SubmissionHoursConstrainType.Equals:
            if (value * 60 !== updateTotalTime) {
              toastService({
                title: translate(
                  'timesheets.feature.update.failed' as TranslationKey,
                ),
                message: (
                  <ToastDescription
                    message={translate(
                      'timesheets.feature.update.fail.exact.required' as TranslationKey,
                      {
                        hours: value,
                      },
                    )}
                  />
                ),
              }).error();

              return false;
            }
            break;

          default:
            return true;
        }
      }
    }

    return true;
  };

  const deleteTimeEntryInTask = (
    task: TimesheetWeeklyRowTask,
    entryDate: string,
    timeEntryId: number,
  ) => {
    const previousTimeEntry = findTimeEntryinState(
      timesheetEntries,
      timeEntryId,
    );

    if (!checkForSubmissionHourRestrict(task, timeEntryId, 0)) {
      return;
    }

    deleteTimeEntryFromState(timeEntryId);

    if (timeEntryId < 0) {
      return;
    }
    TimesheetAPI.deleteTimeEntry(timeEntryId)
      .then(() => {})
      .catch((error) => {
        if (previousTimeEntry) {
          addTimeEntryToState(previousTimeEntry);
        }
        handleError(error, 'delete');
      });
  };

  const updateTimeEntryInTask = (
    task: TimesheetWeeklyRowTask,
    entryDate: string,
    timeEntry: TimesheetTableTimeEntry,
  ) => {
    if (task.approvalStatus === TimesheetStatus.Approved) {
      toastService({
        title: translate('timesheets.feature.update.failed' as TranslationKey),
        message: (
          <ToastDescription
            message={translate(
              'timesheets.feature.update.already.approved' as TranslationKey,
            )}
          />
        ),
      }).error();

      return;
    }

    if (
      !checkForSubmissionHourRestrict(
        task,
        timeEntry.timeEntryId,
        timeEntry.minutes,
      )
    ) {
      return;
    }

    const userCategory = timeEntryCategories.find(
      (c) =>
        c.id === task.defaultCategoryId ||
        c.id === timeEntry.category?.categoryId,
    );

    if (userCategory) {
      setTimesheetWeekRows(() => {
        const rows = updateCategory(
          userCategory,
          timesheetWeekRowsRef.current,
          task,
        );

        timesheetWeekRowsRef.current = rows;

        return rows;
      });
    }
    if (timeEntry.minutes === 0 && timeEntry.timeEntryId > 0) {
      deleteTimeEntryInTask(task, entryDate, timeEntry.timeEntryId);

      return;
    }

    const previousTimeEntry = findTimeEntryinState(
      timesheetEntries,
      timeEntry.timeEntryId,
    );

    if (timeEntry.minutes === 0) {
      return;
    }
    if (timeEntry.timeEntryId > 0) {
      updateTimeEntriesState(timeEntry.timeEntryId, timeEntry);
      if (isTimeEntrySubmittable(timeEntry, configuration)) {
        TimesheetAPI.updateTimeEntry(timeEntry.timeEntryId, {
          ...omit(timeEntry, 'category'),
          categoryId:
            timeEntryCategories.find(
              (c) => c.id === timeEntry.category?.categoryId,
            )?.id ?? null,
          date: entryDate,
          entryType: task.entryType,
          userId: user.userId,
        } as Partial<TimesheetEntryRequest>)
          .then(() => {})
          .catch((error) => {
            if (previousTimeEntry) {
              updateTimeEntriesState(timeEntry.timeEntryId, previousTimeEntry);
            }
            handleError(error, 'update');
          });
      }
    } else {
      updateTimeEntriesState(timeEntry.timeEntryId, {
        ...timeEntry,
        inState: isTimeEntrySubmittable(timeEntry, configuration)
          ? TimeEntryState.Creating
          : TimeEntryState.PartiallyFilled,
      });

      const params = () => {
        switch (task.entryType) {
          case TimesheetEntryType.TaskEntry:
            return { taskId: task.id, projectId: task.project.id };
          case TimesheetEntryType.PhaseEntry:
            return { projectId: task.project.id };
          case TimesheetEntryType.AdhocEntry:
          case TimesheetEntryType.Integration:
            return {
              activityName: task.activityName,
              projectId: task.project?.id,
            };
          default:
            return {};
        }
      };

      if (isTimeEntrySubmittable(timeEntry, configuration)) {
        TimesheetAPI.addTimeEntry(user.userId, {
          ...omit(timeEntry, 'category'),
          ...params(),
          categoryId:
            timeEntryCategories.find(
              (c) => c.id === timeEntry.category?.categoryId,
            )?.id ?? null,
          userId: user.userId,
          date: entryDate,
          entryType: task.entryType,
          sourceType: task.sourceType,
          sourceId:
            task.entryType !== TimesheetEntryType.AdhocEntry
              ? task.sourceId
              : '',
          projectId: params().projectId,
        } as TimesheetEntryRequest)
          .then((timeEntryResponse) => {
            updateTimeEntriesState(timeEntry.timeEntryId, {
              ...timeEntryResponse,
              inState: TimeEntryState.Loaded,
            });

            const modifiedEntries = timesheetEntriesRef.current.map((entry) => {
              if (entry.timeEntryId === timeEntry.timeEntryId) {
                return {
                  ...entry,
                  ...timeEntryResponse,
                };
              }

              return entry;
            }) as TimesheetEntry[];

            if (timesheet && !isMyTimesheet) {
              setTimesheet({
                ...timesheet,
                entries: modifiedEntries,
                updatedAt: Date.now(),
              });
            }
          })
          .catch((error) => {
            deleteTimeEntryFromState(timeEntry.timeEntryId);
            handleError(error, 'add');
          });
      }
    }
  };

  const addTimeEntryInTask = (
    task: TimesheetWeeklyRowTask,
    entryDate: string,
  ) => addTimeEntryToState(getNewTimeEntry(task, entryDate));

  const deleteTask = (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
  ) => {
    const timesheetWeekRow = (timesheetWeekRows ?? []).find((timesheetRow) =>
      isSameTask(timesheetRow.task, task),
    );

    if (!timesheetWeekRow) {
      return;
    }

    const entriesToBeDeleted: TimesheetEntry[] = Object.values(
      timesheetWeekRow.days,
    )
      .reduce((allEntries, dayEntries) => allEntries.concat(...dayEntries), [])
      .map(
        (entry) => findTimeEntryinState(timesheetEntries, entry.timeEntryId)!,
      )
      .filter((x) => Boolean(x));

    deleteTimeEntriesFromState(entriesToBeDeleted, { task, days });

    const timeEntryIdsToBeDeleted = entriesToBeDeleted
      .map((x) => (x.timeEntryId > 0 ? x.timeEntryId : null))
      .filter((x) => Boolean(x));

    if (task.entryType === TimesheetEntryType.Integration) {
      invalidateCalendarIntegration();
    }
    let entriesToBeDeletedFromBE: Promise<null>[] = [];

    if (
      task.entryType === TimesheetEntryType.AdhocEntry &&
      task.activityName.indexOf(',') !== -1
    ) {
      entriesToBeDeletedFromBE = entriesToBeDeleted
        .filter((x) => x.timeEntryId > 0)
        .map((x) => TimesheetAPI.deleteTimeEntry(x.timeEntryId));
    }

    let timeEntryIdsToBeDeletedStringified = 'timeEntryIds=';

    timeEntryIdsToBeDeleted.forEach((x, index) => {
      timeEntryIdsToBeDeletedStringified += String(x);
      if (index !== timeEntryIdsToBeDeleted.length - 1) {
        timeEntryIdsToBeDeletedStringified += ',';
      }
    });

    Promise.all(
      entriesToBeDeletedFromBE.concat(
        TimesheetAPI.deleteTimesheetTask(timeEntryIdsToBeDeletedStringified),
      ),
    )
      .then(() => {
        toastService({
          title: translate(
            'timesheets.text.row_deleted_title' as TranslationKey,
          ),
          message: (
            <ToastDescription
              message={translate(
                'timesheets.text.row_deleted_description' as TranslationKey,
                {
                  name:
                    task.entryType === TimesheetEntryType.TaskEntry
                      ? task.name
                      : task.entryType === TimesheetEntryType.ProjectEntry
                      ? task.project?.name
                      : task.entryType === TimesheetEntryType.PhaseEntry
                      ? task.projectPhase.name
                      : task.activityName,
                },
              )}
            />
          ),
        }).success();
      })
      .catch((error) => {
        addTimeEntriesToState(entriesToBeDeleted);
        handleError(error, 'delete');
      });
  };

  const unsubmitTask = (task: TimesheetWeeklyRowTask) => {
    const timesheetWeekRow = (timesheetWeekRows ?? []).find((timesheetRow) =>
      isSameTask(timesheetRow.task, task),
    );

    if (!timesheetWeekRow) {
      return;
    }

    const entriesToBeUnSubmitted: TimesheetEntry[] = Object.values(
      timesheetWeekRow.days,
    )
      .reduce((allEntries, dayEntries) => allEntries.concat(...dayEntries), [])
      .map(
        (entry) => findTimeEntryinState(timesheetEntries, entry.timeEntryId)!,
      )
      .filter((x) => Boolean(x));

    const timeEntryIdsToBeUnSubmitted = entriesToBeUnSubmitted
      .map((x) => (x.timeEntryId > 0 ? x.timeEntryId : 0))
      .filter((x) => x !== 0);

    if (task.entryType === TimesheetEntryType.Integration) {
      invalidateCalendarIntegration();
    }

    TimesheetAPI.unsubmitTimesheet(
      user.userId,
      startDate.format('YYYY-MM-DD'),
      timeEntryIdsToBeUnSubmitted,
    )
      .then((userTimesheet: Timesheet) => {
        setTimesheet(userTimesheet);
        toastService({
          message: translate(
            'timesheets.succesfully.unsubmit.time' as TranslationKey,
          ),
        }).success();
      })
      .catch((error) => {
        toastService({
          message: translate(
            'timesheets.failed.unsubmit.time' as TranslationKey,
          ),
        }).error();
        handleError(error, 'delete');
      });
  };

  const approveTask = (
    task: TimesheetWeeklyRowTask,
    projectIdValue?: number,
  ) => {
    const timesheetWeekRow = (timesheetWeekRows ?? []).find((timesheetRow) =>
      isSameTask(timesheetRow.task, task),
    );

    if (!timesheetWeekRow) {
      return;
    }

    const entriesToBeApproved: TimesheetEntry[] = Object.values(
      timesheetWeekRow.days,
    )
      .reduce((allEntries, dayEntries) => allEntries.concat(...dayEntries), [])
      .map(
        (entry) => findTimeEntryinState(timesheetEntries, entry.timeEntryId)!,
      )
      .filter((x) => Boolean(x));

    const timeEntryIdsToBeApproved = entriesToBeApproved
      .map((x) => (x.timeEntryId > 0 ? x.timeEntryId : 0))
      .filter((x) => x !== 0);

    if (task.entryType === TimesheetEntryType.Integration) {
      invalidateCalendarIntegration();
    }

    TimesheetAPI.approveTimesheet(
      user.userId,
      startDate.format('YYYY-MM-DD'),
      timesheet!.updatedAt,
      projectIdValue,
      timeEntryIdsToBeApproved,
    )
      .then((userTimesheet: Timesheet) => {
        setTimesheet(userTimesheet);
        toastService({
          message: translate(
            'timesheets.succesfully.approve.time' as TranslationKey,
          ),
        }).success();
      })
      .catch((error) => {
        const {
          response: {
            data: { errors },
          },
        } = error;
        const staleDataError = errors.find(({ reason }) =>
          reason.startsWith('Time entries not in sync for given'),
        );

        if (staleDataError) {
          toastService({
            title: translate(
              'timesheets.toast.titles.approval_stale_error' as TranslationKey,
            ),
            message: translate(
              'timesheets.toast.messages.approval_stale_error' as TranslationKey,
              { userName: fullName(user) },
            ),
          }).info();
          refreshData();
        } else {
          toastService({
            message: translate(
              'timesheets.failed.approve.time' as TranslationKey,
            ),
          }).error();
          handleError(error, 'delete');
        }
      });
  };

  const rejectTask = (
    task: TimesheetWeeklyRowTask,
    content?: string,
    projectIdValue?: number,
  ) => {
    const timesheetWeekRow = (timesheetWeekRows ?? []).find((timesheetRow) =>
      isSameTask(timesheetRow.task, task),
    );

    if (!timesheetWeekRow) {
      return Promise.reject();
    }

    const entriesToBeRejected: TimesheetEntry[] = Object.values(
      timesheetWeekRow.days,
    )
      .reduce((allEntries, dayEntries) => allEntries.concat(...dayEntries), [])
      .map(
        (entry) => findTimeEntryinState(timesheetEntries, entry.timeEntryId)!,
      )
      .filter((x) => Boolean(x));

    const timeEntryIdsToBeRejected = entriesToBeRejected
      .map((x) => (x.timeEntryId > 0 ? x.timeEntryId : 0))
      .filter((x) => x !== 0);

    if (task.entryType === TimesheetEntryType.Integration) {
      invalidateCalendarIntegration();
    }

    return TimesheetAPI.rejectTimesheet(
      user.userId,
      startDate.format('YYYY-MM-DD'),
      timesheet!.updatedAt,
      content,
      projectIdValue,
      timeEntryIdsToBeRejected,
    )
      .then((userTimesheet: Timesheet) => {
        setTimesheet(userTimesheet);
        queryClient.invalidateQueries(
          getRejectNoteQueryKey(startDate.format('YYYY-MM-DD')),
        );
        toastService({
          message: translate(
            'timesheets.succesfully.reject.time' as TranslationKey,
          ),
        }).success();
      })
      .catch((error) => {
        toastService({
          message: translate('timesheets.failed.reject.time' as TranslationKey),
        }).error();
        handleError(error, 'delete');
      });
  };

  const withdrawTask = (
    task: TimesheetWeeklyRowTask,
    projectIdValue?: number,
  ) => {
    const timesheetWeekRow = (timesheetWeekRows ?? []).find((timesheetRow) =>
      isSameTask(timesheetRow.task, task),
    );

    if (!timesheetWeekRow) {
      return;
    }

    const entriesToBeWithdrawn: TimesheetEntry[] = Object.values(
      timesheetWeekRow.days,
    )
      .reduce((allEntries, dayEntries) => allEntries.concat(...dayEntries), [])
      .map(
        (entry) => findTimeEntryinState(timesheetEntries, entry.timeEntryId)!,
      )
      .filter((x) => Boolean(x));

    const timeEntryIdsToBeWithdrawn = entriesToBeWithdrawn
      .map((x) => (x.timeEntryId > 0 ? x.timeEntryId : 0))
      .filter((x) => x !== 0);

    if (task.entryType === TimesheetEntryType.Integration) {
      invalidateCalendarIntegration();
    }

    TimesheetAPI.withdrawTimesheet(
      user.userId,
      startDate.format('YYYY-MM-DD'),
      projectIdValue,
      timeEntryIdsToBeWithdrawn,
    )
      .then((userTimesheet: Timesheet) => {
        setTimesheet(userTimesheet);
        toastService({
          message: translate(
            'timesheets.succesfully.withdraw.approval' as TranslationKey,
          ),
        }).success();
      })
      .catch((error) => {
        toastService({
          message: translate(
            'timesheets.failed.withdraw.approval' as TranslationKey,
          ),
        }).error();
        handleError(error, 'delete');
      });
  };

  const submitTimesheet = useCallback(() => {
    setIsStatusChangeInProgress(true);
    TimesheetAPI.submitTimesheet(user.userId, startDate.format('YYYY-MM-DD'))
      .then((userTimesheet: Timesheet) => {
        setIsStatusChangeInProgress(false);
        setTimesheet(userTimesheet);
        toastService({
          title: translate(
            'timesheets.toast.titles.sent_for_approval' as TranslationKey,
          ),
          message: (
            <ToastDescription
              message={translate(
                'timesheets.toast.messages.sent_for_approval' as TranslationKey,
              )}
            />
          ),
        }).success();
      })
      .catch(() => {
        setIsStatusChangeInProgress(false);
      });
  }, [setIsStatusChangeInProgress, setTimesheet, startDate, user, translate]);

  const unsubmitTimesheet = useCallback(() => {
    setIsStatusChangeInProgress(true);
    TimesheetAPI.unsubmitTimesheet(user.userId, startDate.format('YYYY-MM-DD'))
      .then((userTimesheet: Timesheet) => {
        setIsStatusChangeInProgress(false);
        setTimesheet(userTimesheet);
        toastService({
          title: translate(
            'timesheets.toast.titles.withdraw_from_approval' as TranslationKey,
          ),
          message: (
            <ToastDescription
              message={translate(
                'timesheets.toast.messages.withdraw_from_approval' as TranslationKey,
              )}
            />
          ),
        }).success();
      })
      .catch(() => {
        setIsStatusChangeInProgress(false);
      });
  }, [setIsStatusChangeInProgress, setTimesheet, startDate, user, translate]);

  const withdrawTimesheet = useCallback(
    (projectIdValue?: number) => {
      setIsStatusChangeInProgress(true);
      TimesheetAPI.withdrawTimesheet(
        user.userId,
        startDate.format('YYYY-MM-DD'),
        projectIdValue,
      )
        .then((userTimesheet: Timesheet) => {
          setIsStatusChangeInProgress(false);
          setTimesheet(userTimesheet);
          toastService({
            title: translate(
              'timesheets.toast.titles.withdrawn' as TranslationKey,
            ),
            message: (
              <ToastDescription
                message={translate(
                  'timesheets.toast.messages.withdrawn' as TranslationKey,
                  {
                    b: (chunks) => (
                      <Typography.Text strong>
                        {fullName(user) ?? chunks}
                      </Typography.Text>
                    ),
                  },
                )}
              />
            ),
          }).success();
        })
        .catch(() => {
          setIsStatusChangeInProgress(false);
        });
    },
    [setIsStatusChangeInProgress, setTimesheet, startDate, user, translate],
  );

  const approveTimesheet = useCallback(
    (projectIdValue?: number) => {
      setIsStatusChangeInProgress(true);
      TimesheetAPI.approveTimesheet(
        user.userId,
        startDate.format('YYYY-MM-DD'),
        timesheet!.updatedAt,
        projectIdValue,
      )
        .then((userTimesheet: Timesheet) => {
          setIsStatusChangeInProgress(false);
          setTimesheet(userTimesheet);
          toastService({
            title: translate(
              'timesheets.toast.titles.approved' as TranslationKey,
            ),
            message: translate(
              'timesheets.toast.messages.approved' as TranslationKey,
              {
                b: (chunks) => (
                  <Typography.Text strong>
                    {fullName(user) ?? chunks}
                  </Typography.Text>
                ),
              },
            ),
          }).success();
        })
        .catch((error) => {
          setIsStatusChangeInProgress(false);
          const {
            response: {
              data: { errors },
            },
          } = error;
          const staleDataError = errors.find(({ reason }) =>
            reason.startsWith('Time entries not in sync for given'),
          );

          if (staleDataError) {
            toastService({
              title: translate(
                'timesheets.toast.titles.approval_stale_error' as TranslationKey,
              ),
              message: translate(
                'timesheets.toast.messages.approval_stale_error' as TranslationKey,
                { userName: fullName(user) },
              ),
            }).info();
            refreshData();
          }
        });
    },
    [
      setIsStatusChangeInProgress,
      setTimesheet,
      startDate,
      user,
      translate,
      timesheet,
      refreshData,
    ],
  );

  const rejectTimesheet = useCallback(
    (content?: string, projectIdValue?: number) => {
      return TimesheetAPI.rejectTimesheet(
        user.userId,
        startDate.format('YYYY-MM-DD'),
        timesheet!.updatedAt,
        content,
        projectIdValue,
      )
        .then((userTimesheet: Timesheet) => {
          setTimesheet(userTimesheet);
          queryClient.invalidateQueries(
            getRejectNoteQueryKey(startDate.format('YYYY-MM-DD')),
          );
          toastService({
            title: translate(
              'timesheets.toast.titles.rejected' as TranslationKey,
            ),
            message: translate(
              'timesheets.toast.messages.rejected' as TranslationKey,
              {
                b: (chunks) => (
                  <Typography.Text strong>
                    {fullName(user) ?? chunks}
                  </Typography.Text>
                ),
              },
            ),
          }).success();
        })
        .catch((error) => {
          const {
            response: {
              data: { errors },
            },
          } = error;
          const staleDataError = errors.find(({ reason }) =>
            reason.startsWith('Time entries not in sync for given'),
          );

          if (staleDataError) {
            toastService({
              title: translate(
                'timesheets.toast.titles.approval_stale_error' as TranslationKey,
              ),
              message: translate(
                'timesheets.toast.messages.approval_stale_error' as TranslationKey,
                { userName: fullName(user) },
              ),
            }).info();
            refreshData();
          }
        });
    },
    [user, startDate, timesheet, queryClient, translate, refreshData],
  );

  const sendReminder = useCallback(() => {
    TimesheetAPI.sendReminder(
      user.userId,
      startDate.format('YYYY-MM-DD'),
      projectId,
    ).then((reminder) => {
      if (timesheet) {
        setTimesheet({
          ...timesheet,
          reminders: timesheet.reminders.concat(reminder),
        });
        toastService({
          title: translate(
            'timesheets.toast.titles.reminder_sent' as TranslationKey,
          ),
          message: (
            <ToastDescription
              message={translate(
                'timesheets.toast.messages.reminder_sent' as TranslationKey,
                {
                  b: (chunks) => (
                    <Typography.Text strong>
                      {fullName(user) ?? chunks}
                    </Typography.Text>
                  ),
                },
              )}
            />
          ),
        }).success();
      }
    });
  }, [user, startDate, projectId, timesheet, translate]);

  const addIntegrationTaskToTimesheet = async (
    tasks: TimesheetCalendarIntegrationEntry[],
  ) => {
    const entryTask = tasks.map(convertRowDataToTimesheet);

    const newEntries = addGenericTaskToTimesheet(
      removeDuplicateEntry(entryTask),
      TimesheetEntryType.Integration,
    );

    setTimesheetEntries([...timesheetEntries, ...newEntries.timeEntries]);
    setTimesheetWeekRows([...timesheetWeekRows, ...newEntries.weeklyRows]);
    tasks.forEach((task) =>
      addTimeEntryToState({
        ...task,
        inState: TimeEntryState.Loaded,
        project: task?.project?.id === 1 ? undefined : task.project,
      }),
    );
  };

  const addProjectToTimesheet = (projectToBeAdded: Option) => {
    const newTask: ProjectRow = taskAsProjectRow({
      project: { label: projectToBeAdded.label, value: projectToBeAdded.value },
    });
    const previousDefault = timesheetWeekRowsRef.current.find((e) =>
      isSameTask(e.task, newTask),
    );

    const newTaskWithCategory: ProjectRow = {
      ...newTask,
      defaultCategoryId:
        newTask.defaultCategoryId || previousDefault?.task.defaultCategoryId,
    };

    const entries = getDays(startDate, 7).map((x) =>
      getNewTimeEntry(newTaskWithCategory, x.format('YYYY-MM-DD')),
    );

    const newProjectRow = {
      task: newTaskWithCategory,
      days: entries.reduce(
        (result, current) => ({ ...result, [current.date]: [current] }),
        {},
      ),
    };

    setTimesheetWeekRows([...timesheetWeekRows, newProjectRow]);
    setTimesheetEntries([...timesheetEntries, ...entries]);
  };

  const projectOptions: Option[] = useMemo(() => {
    return (projects ?? [])?.map((projectT) => ({
      label: projectT.projectName,
      value: projectT.projectId,
      imageUrl: projectT.customer?.companyLogoUrl,
    }));
  }, [projects]);

  const addEntityToProject = (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
    entity,
  ) => {
    const weekRows = timesheetWeekRows.reduce(
      (previous, curr: TimesheetWeeklyRow) => {
        if (isEqual(task, curr.task) && isEqual(days, curr.days)) {
          let newTask;

          if (
            entity.type === 'TASK_ENTRY' ||
            entity.type === 'MILESTONE_ENTRY'
          ) {
            newTask = taskAsTimesheetTask(entity.extra);
          }

          if (entity.type === 'PHASE_ENTRY') {
            newTask = taskAsPhaseEntry(entity.extra);
          }

          const entries = getDays(startDate, 7).map((x) =>
            getNewTimeEntry(newTask, x.format('YYYY-MM-DD')),
          );

          setTimesheetEntries([...timesheetEntries, ...entries]);

          return [
            ...previous,
            {
              days: entries.reduce(
                (result, current) => ({ ...result, [current.date]: [current] }),
                {},
              ),
              task: newTask,
            },
          ];
        }

        return [...previous, { ...curr }];
      },
      [] as TimesheetWeeklyRow[],
    );

    setTimesheetWeekRows(weekRows);
  };

  const changeProject = (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
    projectToBeAdded: Option,
  ) => {
    const weekRows = timesheetWeekRows.reduce(
      (previous, curr: TimesheetWeeklyRow) => {
        if (isEqual(task, curr.task) && isEqual(days, curr.days)) {
          const newTask: ProjectRow = taskAsProjectRow({
            project: {
              label: projectToBeAdded.label,
              value: projectToBeAdded.value,
            },
          });

          const entries = getDays(startDate, 7).map((x) =>
            getNewTimeEntry(newTask, x.format('YYYY-MM-DD')),
          );

          setTimesheetEntries([...timesheetEntries, ...entries]);

          return [
            ...previous,
            {
              days: entries.reduce(
                (result, current) => ({ ...result, [current.date]: [current] }),
                {},
              ),
              task: newTask,
            },
          ];
        }

        return [...previous, { ...curr }];
      },
      [] as TimesheetWeeklyRow[],
    );

    setTimesheetWeekRows(weekRows);
  };

  const addActivityToProject = (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
    activityName,
  ) => {
    const previousDefault = timesheetWeekRowsRef.current.find((e) =>
      isSameTask(e.task, task),
    );

    const weekRows = timesheetWeekRows.reduce(
      (previous, curr: TimesheetWeeklyRow) => {
        if (isEqual(task, curr.task) && isEqual(days, curr.days)) {
          const newTask = {
            project:
              curr.task.project !== undefined
                ? {
                    ...curr.task.project,
                  }
                : undefined,
            defaultCategoryId: previousDefault?.task.defaultCategoryId,
            entryType: TimesheetEntryType.AdhocEntry,
            activityName,
            sourceType: TimesheetUISourceType.Adhoc,
            defaultBillable: curr.task.project !== undefined,
          } as TimesheetAdhocTask;

          const entries = getDays(startDate, 7).map((x) =>
            getNewTimeEntry(newTask, x.format('YYYY-MM-DD')),
          );

          setTimesheetEntries([...timesheetEntries, ...entries]);

          return [
            ...previous,
            {
              days: entries.reduce(
                (result, current) => ({ ...result, [current.date]: [current] }),
                {},
              ),
              task: newTask,
            },
          ];
        }

        return [...previous, { ...curr }];
      },
      [] as TimesheetWeeklyRow[],
    );

    setTimesheetWeekRows(weekRows);
  };

  return {
    projects,
    timesheet,
    timeEntryCategories,
    isTimesheetLoading,
    isStatusChangeInProgress,
    timesheetWeekRows,
    timesheetEntries,
    addTasksToTimesheet,
    addAdhocActivitiesToTimesheet,
    editAdhocTaskName,
    getSummaryDetail,
    updateTimeEntryInTask,
    addTimeEntryInTask,
    deleteTimeEntryInTask,
    deleteTask,
    unsubmitTask,
    approveTask,
    rejectTask,
    withdrawTask,
    submitTimesheet,
    unsubmitTimesheet,
    withdrawTimesheet,
    approveTimesheet,
    rejectTimesheet,
    sendReminder,
    getPreviousWeekTasks,
    timeSheetSummary: getSummaryDetail(timesheetWeekRows ?? []),
    categories: _categories,
    addIntegrationTaskToTimesheet,
    addProjectToTimesheet,
    projectOptions,
    addEntityToProject,
    changeProject,
    addActivityToProject,
  };
};
